






















































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Action, State } from 'vuex-class';
import { RootState } from '@/store/store';
import { ApiState, SortOrder, SortSpecs } from '@/store/types/general.types';
import {
  EventStatus,
  Pagination
} from '@/store/modules/admin/types/admin.types';
import { Debounce } from '@/jbi-shared/util/debounce.vue-decorator';
import BasePaginatorHoc from '@/components/base/BasePaginatorHoc.vue';
import EventsList from './components/EventsList.vue';
import {
  JbicEventParams,
  Event
} from '@/store/modules/admin/types/admin.types';
import { toTitleCase } from '@/jbi-shared/util/string.utils';
import dayjs from 'dayjs';
import { checkJbicPermission } from '@/utils/rbac.util';
import { JbicPermissions } from '@/store/modules/roles-and-permissions/types/roles-and-permissions.types';
import { subscribeToRealTime } from '@/utils/real-time.util';
import { useWebsocket } from '@/utils/websocket.util';

const getInitFormParams = (): JbicEventParams => ({
  eventName: '',
  dates: [],
  status: EventStatus.DRAFT,
  page: 1,
  limit: 50,
  sortColumn: 'event.updated_at',
  sortOrder: SortOrder.DESC
});

@Component({
  components: {
    BasePaginatorHoc,
    EventsList
  }
})
export default class GroupEventsTab extends Vue {
  @Prop(Number) groupId!: number;

  // get jbic events
  @Action('admin/getJbicGroupEventsWithPagination')
  getJbicGroupEventsWithPagination!: (params: {
    groupId: number;
    params?: JbicEventParams;
  }) => void;

  @State(
    ({ admin }: RootState) => admin.apiState.getJbicGroupEventsWithPagination
  )
  jbicGroupEventsWithPaginationState!: ApiState;

  @State(({ admin }: RootState) => admin.jbicGroupEventsWithPagination)
  jbicGroupEventsWithPagination!: Pagination<Event>;

  get EventsList() {
    return EventsList;
  }

  get StatusList() {
    return Object.values(EventStatus)
      .map((status) => toTitleCase(status, '_'))
      .sort();
  }

  get isJbicEventCreateRole(): boolean {
    return checkJbicPermission(JbicPermissions.EVENT_CREATE, this.groupId);
  }

  get isJbicEventUpdateRole(): boolean {
    return checkJbicPermission(JbicPermissions.EVENT_UPDATE, this.groupId);
  }

  get isJbicEventDeleteRole(): boolean {
    return checkJbicPermission(JbicPermissions.EVENT_DELETE, this.groupId);
  }

  get isJbicEventViewRole(): boolean {
    return checkJbicPermission(JbicPermissions.EVENT_READ, this.groupId);
  }

  get isJbicEventManageRole(): boolean {
    return checkJbicPermission(JbicPermissions.EVENT_MANAGE, this.groupId);
  }

  // pagination
  public perPage = 50;
  public page = 1;
  public sortColumn: string = 'updated_at';
  public sortOrder: SortOrder = SortOrder.DESC;
  public totalItems: number = 0;

  // filter
  public filterParams: JbicEventParams = getInitFormParams();
  public expandGroupFilter = false;

  // params
  public events: Event[] = [];
  public eventStatusValues: string[] = this.StatusList;
  private unsubscribeRealtime: (() => void) | undefined;

  async mounted() {
    this.getJbicGroupEventsWithPaginationHandler();
    if (this.isJbicEventManageRole) {
      this.filterParams.status = EventStatus.AWAITING_APPROVAL;
    }
    if (this.groupId) {
      this.unsubscribeRealtime = await subscribeToRealTime.call(
        this,
        'groupEvent-' + this.groupId,
        async () => {
          this.getJbicGroupEventsWithPaginationHandler();
        }
      );
    }
  }

  beforeDestroy() {
    if (this.unsubscribeRealtime) {
      this.unsubscribeRealtime(); // remove listener
      const { disconnectWs } = useWebsocket.call(this);
      disconnectWs();
    }
  }

  getJbicGroupEventsWithPaginationHandler() {
    const dates: string[] = this.filterParams.dates ?? [];

    this.getJbicGroupEventsWithPagination({
      groupId: this.groupId,
      params: {
        eventName: this.filterParams.eventName,
        dates,
        status: this.filterParams.status,
        limit: this.filterParams.limit,
        page: this.filterParams.page,
        sortColumn: this.filterParams.sortColumn,
        sortOrder: this.filterParams.sortOrder
      }
    });
  }

  resetFilter() {
    this.filterParams = getInitFormParams();
  }

  formatDateRange(dates: Date[]) {
    if (!dates || dates.length !== 2) {
      return '';
    }
    const start = dayjs(dates[0]).format('DD MMM YYYY');
    const end = dayjs(dates[1]).format('DD MMM YYYY');
    return `${start} - ${end}`;
  }

  @Watch('jbicGroupEventsWithPaginationState')
  onJbicGroupEventsWithPaginationState() {
    if (
      !this.jbicGroupEventsWithPaginationState ||
      this.jbicGroupEventsWithPaginationState.loading ||
      !this.jbicGroupEventsWithPaginationState.success ||
      !this.jbicGroupEventsWithPagination
    ) {
      return;
    }

    this.events = this.jbicGroupEventsWithPagination.items;
    this.totalItems = this.jbicGroupEventsWithPagination.totalItems;
  }

  @Watch('filterParams', { deep: true })
  @Debounce(500)
  onFilterParams() {
    this.perPage = this.filterParams.limit ?? 12;
    this.page = this.filterParams.page ?? 1;
    this.sortColumn = this.filterParams.sortColumn ?? 'updated_at';
    this.sortOrder = this.filterParams.sortOrder ?? SortOrder.DESC;

    this.getJbicGroupEventsWithPaginationHandler();
  }

  public async handleSort(data: SortSpecs) {
    this.filterParams.sortColumn = data.sortColumn;
    this.filterParams.sortOrder = data.sortOrder;

    this.getJbicGroupEventsWithPaginationHandler();

    this.$router.push({
      path: this.$route.path,
      query: {
        tab: 'Events',
        eventName: this.$route.query.eventName || this.filterParams.eventName!,
        dates: this.$route.query.dates || this.filterParams.dates!,
        status: this.$route.query.status || this.filterParams.status!,
        limit: (this.$route.query.limit as string) || this.perPage.toString(),
        page: (this.$route.query.page as string) || this.page.toString(),
        sortColumn: this.sortColumn,
        sortOrder: this.sortOrder
      }
    });
  }

  public async handlePaginator({
    perPage,
    page
  }: {
    perPage: number;
    page: number;
  }) {
    if (this.$route.query.tab !== 'Events') {
      return;
    }
    this.filterParams.limit = perPage || +this.$route.query.limit;
    this.filterParams.page = page || +this.$route.query.page;

    if (
      this.filterParams.groupName?.length ||
      this.filterParams.eventName?.length ||
      this.filterParams.status?.length
    ) {
      this.expandGroupFilter = true;
    }

    this.getJbicGroupEventsWithPaginationHandler();

    this.$router.push({
      path: this.$route.path,
      query: {
        tab: 'Events',
        groupName: this.$route.query.groupName || this.filterParams.groupName!,
        eventName: this.$route.query.eventName || this.filterParams.eventName!,
        status: this.$route.query.status || this.filterParams.status!,
        limit: this.perPage.toString(),
        page: this.page.toString(),
        sortColumn: (this.$route.query.sortColumn as string) || this.sortColumn,
        sortOrder: (this.$route.query.sortOrder as SortOrder) || this.sortOrder
      }
    });

    this.expandGroupFilter = false;

    if (
      this.filterParams.groupName?.length ||
      this.filterParams.eventName?.length ||
      this.filterParams.status?.length
    ) {
      this.expandGroupFilter = true;
    }
  }
}
